import { useCallback, useRef, useState } from 'react';

import type { WritebackProvider } from '@/react-query/videa-notes/types';
import { fetchWritebackPrecheck } from '@/react-query/videa-notes/useSubmitWriteback';

export interface ProviderDialogState {
  open: boolean;
  providers: WritebackProvider[];
}

export class ProviderSelectionCancelledError extends Error {
  constructor() {
    super('PROVIDER_SELECTION_CANCELLED');
    this.name = 'ProviderSelectionCancelledError';
  }
}

export class PrecheckFailedError extends Error {
  constructor(
    public reason: string,
    message: string,
  ) {
    super(message);
    this.name = 'PrecheckFailedError';
  }
}

interface UseWritebackPrecheckProps {
  onProviderSelected?: (providerPmsId: string) => void;
  onProviderDialogCancelled?: () => void;
}

/**
 * Shared hook that runs the writeback precheck and manages provider selection dialog state.
 *
 * `runPrecheck` resolves with `{ providerPmsId?: string }` on success,
 * or throws `PrecheckFailedError` / `ProviderSelectionCancelledError`.
 */
export const useWritebackPrecheck = ({
  onProviderSelected,
  onProviderDialogCancelled,
}: UseWritebackPrecheckProps = {}) => {
  const providerPromiseRef = useRef<{
    resolve: (providerPmsId: string) => void;
    reject: (error: Error) => void;
  } | null>(null);

  const [providerDialogState, setProviderDialogState] =
    useState<ProviderDialogState>({
      open: false,
      providers: [],
    });

  const handleProviderConfirm = useCallback(
    (providerPmsId: string) => {
      onProviderSelected?.(providerPmsId);
      setProviderDialogState({ open: false, providers: [] });
      providerPromiseRef.current?.resolve(providerPmsId);
      providerPromiseRef.current = null;
    },
    [onProviderSelected],
  );

  const handleProviderCancel = useCallback(() => {
    onProviderDialogCancelled?.();
    setProviderDialogState({ open: false, providers: [] });
    providerPromiseRef.current?.reject(new ProviderSelectionCancelledError());
    providerPromiseRef.current = null;
  }, [onProviderDialogCancelled]);

  const runPrecheck = useCallback(
    async (
      clinicalNoteId: string,
      practiceId: string,
      organizationId: string,
    ): Promise<{ providerPmsId?: string }> => {
      const precheck = await fetchWritebackPrecheck(
        clinicalNoteId,
        practiceId,
        organizationId,
      );

      if (precheck.canWriteback) {
        return {};
      }

      if (precheck.reason === 'NO_PMS_PATIENT') {
        throw new PrecheckFailedError(
          'NO_PMS_PATIENT',
          'No matching patient found in PMS',
        );
      }

      if (precheck.reason === 'NO_PROVIDER') {
        if (!precheck.providers || precheck.providers.length === 0) {
          throw new PrecheckFailedError(
            'NO_PROVIDER_NO_SUGGESTIONS',
            'No provider available for writeback',
          );
        }

        const providerPmsId = await new Promise<string>((resolve, reject) => {
          providerPromiseRef.current = { resolve, reject };
          setProviderDialogState({
            open: true,
            providers: precheck.providers,
          });
        });

        return { providerPmsId };
      }

      throw new PrecheckFailedError(
        precheck.reason ?? 'UNKNOWN',
        'Writeback is not available',
      );
    },
    [],
  );

  return {
    runPrecheck,
    providerDialogState,
    handleProviderConfirm,
    handleProviderCancel,
  };
};
